// components/Dashboard/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import './Dashboard.css';

// Components
import ChannelModal from '../Modals/ChannelModal/ChannelModal';
import ContentModal from '../Modals/ContentModal/ContentModal';
import HistoryModal from '../Modals/HistoryModal/HistoryModal';

// Tabs
import LibraryTab from './tabs/LibraryTab';
import ChannelsTab from './tabs/ChannelsTab';
import AutomationTab from './tabs/AutomationTab';
import StatisticsTab from './tabs/StatisticsTab';
import ProfileTab from './tabs/ProfileTab';
import AdminPanel from '../AdminPanel/AdminPanel';
import TagsTab from './tabs/TagsTab';
import SiteTrackingTab from './tabs/SiteTrackingTab';
import LiveStudioTab from './tabs/LiveStudioTab';
import MailManagementTab from './tabs/MailManagementTab';
import CopyrightFreeMusicTab from './tabs/CopyrightFreeMusicTab';
import BrandsTab from './tabs/BrandsTab';
import QuickShareTab from './tabs/QuickShareTab';
import AIVideoTab from './tabs/AIVideoTab';

// Config
import API_URL, { AUTH_URL, BASE_URL } from '../../config';

// ============ SVG ICON COMPONENTS ============
const Icons = {
  Library: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
      <path d="M8 7h6" />
      <path d="M8 11h8" />
    </svg>
  ),
  Channels: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
      <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.4" />
      <circle cx="12" cy="12" r="2" />
      <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.4" />
      <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19" />
    </svg>
  ),
  Automation: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4" />
      <line x1="8" y1="16" x2="8" y2="16" />
      <line x1="16" y1="16" x2="16" y2="16" />
      <path d="M9 20v1" />
      <path d="M15 20v1" />
    </svg>
  ),
  Tags: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
      <path d="M7 7h.01" />
    </svg>
  ),
  Statistics: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" />
      <path d="M18 17V9" />
      <path d="M13 17V5" />
      <path d="M8 17v-3" />
    </svg>
  ),
  LiveStudio: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
      <rect x="2" y="6" width="14" height="12" rx="2" />
      <circle cx="9" cy="12" r="2" fill="currentColor" />
    </svg>
  ),
  Admin: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  ),
  Mail: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  ),
  SiteTracking: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  Profile: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M20 21a8 8 0 0 0-16 0" />
    </svg>
  ),
  Crown: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z" />
      <path d="M3 20h18" />
    </svg>
  ),
  Logout: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16,17 21,12 16,7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  Close: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Music: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  ),
  Menu: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  ),
  ChevronDown: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Brand: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
  QuickShare: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  ),
  AIVideo: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2" />
      <path d="M9.5 8.5l7 3.5-7 3.5V8.5z" />
      <path d="M17 2l2 2-2 2" />
      <path d="M19 4h-4" />
    </svg>
  )

};

const Dashboard = ({ user, onLogout }) => {

  const [selectedTagForLibrary, setSelectedTagForLibrary] = useState(null);
  const [pendingAutomationEdit, setPendingAutomationEdit] = useState(null);
  // State Management
  const [activeTab, setActiveTab] = useState('library');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Submenu State
  const [expandedMenus, setExpandedMenus] = useState(['library']); // Varsayılan olarak İçerikler açık

  // Data States
  const [channels, setChannels] = useState([]);
  const [contentLibrary, setContentLibrary] = useState([]);
  const [tags, setTags] = useState([]);
  const [automationRules, setAutomationRules] = useState([]);
  const [statistics, setStatistics] = useState({
    summary: {},
    content: [],
    channels: []
  });
  const [channelStats, setChannelStats] = useState({});
  const [mtprotoStatus, setMtprotoStatus] = useState(null);

  // Loading States
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState({});

  // Modal States
  const [isChannelModalOpen, setIsChannelModalOpen] = useState(false);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedContentForHistory, setSelectedContentForHistory] = useState(null);
  const [editingChannelId, setEditingChannelId] = useState(null);
  const [editingContentId, setEditingContentId] = useState(null);
  const [contentPagination, setContentPagination] = useState(null);
  const [contentTagStats, setContentTagStats] = useState([]);

  // Form States
  const [channelForm, setChannelForm] = useState({
    name: '',
    chat_id: '',
    channel_tags: [],
    description: ''
  });

  const [contentForm, setContentForm] = useState({
    title: '',
    content: '',
    tagIds: [],
    media: null,
    buttons: []
  });

  // ButtonForm state for ContentModal
  const [buttonForm, setButtonForm] = useState([]);

  // Load initial data based on active tab
  useEffect(() => {
    loadTabData();
  }, [activeTab]);

  // Initial load
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load all initial data
  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchChannels(),
        fetchContentLibrary(),
        fetchTags(),
        fetchStatistics(),
        checkMtprotoStatus()
      ]);
    } catch (error) {
      console.error('Initial data load error:', error);
      showMessage('Veriler yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load data based on active tab
  const loadTabData = async () => {
    switch (activeTab) {
      case 'library':
        await fetchContentLibrary();
        break;
      case 'channels':
        if (channels.length === 0) {
          await Promise.all([
            fetchChannels(false),
            fetchTags()
          ]);
        } else {
          await fetchTags();
        }
        break;
      case 'tags':
        await Promise.all([
          fetchTags(),
          contentTagStats.length === 0 ? fetchContentLibrary() : Promise.resolve()
        ]);
        break;
      case 'automation':
        await Promise.all([
          fetchAutomationRules(),
          channels.length === 0 ? fetchChannels(false) : Promise.resolve(),
          fetchContentLibrary()
        ]);
        break;
      case 'statistics':
        await fetchStatistics();
        break;
      default:
        break;
    }
  };

  // Message handler
  const showMessage = (text, type = 'info', duration = 4000) => {
    switch (type) {
      case 'success':
        toast.success(text, { duration });
        break;
      case 'error':
        toast.error(text, { duration });
        break;
      case 'warning':
        toast(text, {
          icon: '⚠️',
          duration,
          style: {
            background: '#FFA500',
            color: '#fff',
          }
        });
        break;
      default:
        toast(text, { duration });
    }
  };

  // API Functions
  const fetchChannels = async (forceRefresh = false) => {
    try {
      const response = await axios.get(`${API_URL}/channels`);
      const channelsData = response.data.channels || [];
      setChannels(channelsData);

      if (channelsData.length > 0) {
        const newChannelStats = {};
        channelsData.forEach(channel => {
          if (channel.total_views !== undefined || channel.member_count > 0) {
            newChannelStats[channel.id] = {
              subscriberCount: channel.member_count || 0,
              totalViews: channel.total_views || 0,
              totalForwards: channel.total_forwards || 0,
              totalReactions: channel.total_reactions || 0,
              totalComments: channel.total_comments || 0,
              lastUpdate: channel.stats_updated_at || new Date().toISOString()
            };
          }
        });
        setChannelStats(newChannelStats);
      }
    } catch (error) {
      console.error('Channels fetch error:', error);
      showMessage('Kanallar yüklenemedi', 'error');
    }
  };

  const handleRefreshChannels = async () => {
    try {
      showMessage('Kanal istatistikleri güncelleniyor...', 'info');
      const refreshResponse = await axios.post(`${API_URL}/channels/refresh-all`);

      if (refreshResponse.data.success) {
        showMessage(`${refreshResponse.data.updated} kanal istatistiği güncellendi!`, 'success');

        const response = await axios.get(`${API_URL}/channels`);
        const channelsData = response.data.channels || [];
        setChannels(channelsData);

        const newChannelStats = {};
        channelsData.forEach(channel => {
          newChannelStats[channel.id] = {
            subscriberCount: channel.member_count || 0,
            totalViews: channel.total_views || 0,
            totalForwards: channel.total_forwards || 0,
            totalReactions: channel.total_reactions || 0,
            totalComments: channel.total_comments || 0,
            lastUpdate: channel.stats_updated_at || new Date().toISOString()
          };
        });

        setChannelStats(newChannelStats);
        await fetchStatistics();
      }

      if (mtprotoStatus?.connected && channels.length > 0) {
        showMessage('Detaylı istatistikler güncelleniyor...', 'info');

        for (const channel of channels) {
          try {
            await fetchChannelStats(channel.id);
            await new Promise(resolve => setTimeout(resolve, 200));
          } catch (error) {
            console.error(`Stats error for ${channel.name}:`, error);
          }
        }
      }
    } catch (error) {
      console.error('Refresh channels error:', error);
      showMessage('Güncelleme sırasında hata oluştu', 'error');
    }
  };

  const fetchContentLibrary = async (tag = '', page = 1, sort = 'updated_at_desc') => {
    try {
      const params = new URLSearchParams();
      if (tag) params.set('search', tag);
      params.set('page', page);
      params.set('limit', 20);
      params.set('sort', sort);

      const url = `${API_URL}/content-library?${params.toString()}`;
      const response = await axios.get(url);
      setContentLibrary(response.data.contents || []);
      setContentPagination(response.data.pagination || null);
      setContentTagStats(response.data.tagStats || []);
    } catch (error) {
      console.error('Content library fetch error:', error);
      showMessage('İçerik listesi yüklenemedi', 'error');
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API_URL}/tags`);
      setTags(response.data.tags || []);
    } catch (error) {
      console.error('Tags fetch error:', error);
    }
  };

  const fetchAutomationRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/automation`);
      setAutomationRules(response.data.rules || []);
    } catch (error) {
      console.error('Automation rules fetch error:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const [summaryRes, contentRes, channelRes] = await Promise.all([
        axios.get(`${API_URL}/statistics/summary`),
        axios.get(`${API_URL}/statistics/content`),
        axios.get(`${API_URL}/statistics/channels`)
      ]);

      setStatistics({
        summary: summaryRes.data.summary || {},
        content: contentRes.data.statistics || [],
        channels: channelRes.data.statistics || []
      });
    } catch (error) {
      console.error('Statistics fetch error:', error);
    }
  };

  const checkMtprotoStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/mtproto/status`);
      setMtprotoStatus(response.data);
    } catch (error) {
      console.error('MTProto status check error:', error);
    }
  };

  const fetchChannelStats = async (channelId) => {
    setLoadingStats(prev => ({ ...prev, [channelId]: true }));
    try {
      const response = await axios.get(`${API_URL}/channels/${channelId}/stats`);
      const stats = response.data;

      if (stats.error) {
        showMessage(`İstatistikler kısmen yüklenemedi: ${stats.error}`, 'warning', 5000);
      }

      setChannelStats(prev => ({ ...prev, [channelId]: stats }));
    } catch (error) {
      console.error('Channel stats fetch error:', error);
      showMessage('İstatistikler yüklenemedi', 'error');
    } finally {
      setLoadingStats(prev => ({ ...prev, [channelId]: false }));
    }
  };

  // Channel Operations
  const handleChannelSubmit = async (e) => {
    setChannelForm({ name: '', chat_id: '', channel_tags: [], description: '' });
    setIsChannelModalOpen(false);
    setEditingChannelId(null);
    await fetchChannels();
  };

  const handleEditChannel = (channel) => {
    const tagIds = channel.channel_tags ? channel.channel_tags.map(t => t.id) : [];
    setChannelForm({
      name: channel.name,
      chat_id: channel.chat_id,
      channel_tags: tagIds,
      description: channel.description || '',
      photo_url: channel.photo_url || null
    });
    setEditingChannelId(channel.id);
    setIsChannelModalOpen(true);
  };

  const handleDeleteChannel = async (id) => {
    if (window.confirm('Bu kanalı silmek istediğinize emin misiniz?')) {
      try {
        await axios.delete(`${API_URL}/channels/${id}`);
        await fetchChannels();
        showMessage('Kanal silindi!', 'success');
      } catch (error) {
        showMessage(`Silme hatası: ${error.message}`, 'error');
      }
    }
  };

  // Content Operations
  const handleContentSubmit = async (formData) => {
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('content', formData.content || '');

      // YouTube alanları
      if (formData.youtube_title !== undefined) {
        submitData.append('youtube_title', formData.youtube_title || '');
      }
      if (formData.youtube_description !== undefined) {
        submitData.append('youtube_description', formData.youtube_description || '');
      }

      // X (Twitter) alanları
      if (formData.x_text !== undefined) {
        submitData.append('x_text', String(formData.x_text || ''));
      }
      if (formData.is_x_enabled !== undefined) {
        submitData.append('is_x_enabled', formData.is_x_enabled ? '1' : '0');
      }

      // TikTok alanları
      if (formData.tiktok_caption !== undefined) {
        submitData.append('tiktok_caption', formData.tiktok_caption || '');
      }
      if (formData.tiktok_hashtags !== undefined) {
        submitData.append('tiktok_hashtags', formData.tiktok_hashtags || '');
      }
      if (formData.tiktok_allow_comments !== undefined) {
        submitData.append('tiktok_allow_comments', formData.tiktok_allow_comments ? '1' : '0');
      }
      if (formData.tiktok_allow_duet !== undefined) {
        submitData.append('tiktok_allow_duet', formData.tiktok_allow_duet ? '1' : '0');
      }
      if (formData.is_tiktok_enabled !== undefined) {
        submitData.append('is_tiktok_enabled', formData.is_tiktok_enabled ? '1' : '0');
      }

      // Instagram alanları
      if (formData.instagram_caption !== undefined) {
        submitData.append('instagram_caption', formData.instagram_caption || '');
      }
      if (formData.instagram_content_type !== undefined) {
        submitData.append('instagram_content_type', formData.instagram_content_type || 'post');
      }
      if (formData.instagram_first_comment !== undefined) {
        submitData.append('instagram_first_comment', formData.instagram_first_comment || '');
      }
      if (formData.instagram_collaborators !== undefined) {
        submitData.append('instagram_collaborators', formData.instagram_collaborators || '');
      }
      if (formData.instagram_user_tags !== undefined) {
        submitData.append('instagram_user_tags', formData.instagram_user_tags || '');
      }
      if (formData.is_instagram_enabled !== undefined) {
        submitData.append('is_instagram_enabled', formData.is_instagram_enabled ? '1' : '0');
      }

      // Tag'ler
      if (formData.tagIds && formData.tagIds.length > 0) {
        const cleanedTagIds = [...new Set(formData.tagIds.map(id => parseInt(id)))];
        submitData.append('tagIds', JSON.stringify(cleanedTagIds));
      }

      // Medya dosyalarını ekle
      if (formData.media) {
        if (Array.isArray(formData.media)) {
          formData.media.forEach((file) => {
            if (file instanceof File) {
              submitData.append('media', file);
            }
          });
        } else if (formData.media instanceof File) {
          submitData.append('media', formData.media);
        }
      }

      // Butonlar
      if (formData.buttons && Array.isArray(formData.buttons) && formData.buttons.length > 0) {
        submitData.append('buttons', JSON.stringify(formData.buttons));
      }

      // Video watermark bilgileri
      if (formData.videoWatermarks && Object.keys(formData.videoWatermarks).length > 0) {
        submitData.append('videoWatermarks', JSON.stringify(formData.videoWatermarks));
      }

      // Watermark logo dosyası
      if (formData.watermarkLogoFile && formData.watermarkLogoFile instanceof File) {
        submitData.append('watermarkLogo', formData.watermarkLogoFile);
      }

      const url = editingContentId
        ? `${API_URL}/content-library/${editingContentId}`
        : `${API_URL}/content-library`;

      const method = editingContentId ? 'PUT' : 'POST';

      const token = localStorage.getItem('token');

      const response = await axios({
        method: method,
        url: url,
        data: submitData,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percentComplete = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setContentForm(prev => ({
              ...prev,
              uploadProgress: percentComplete
            }));
          }
        }
      });

      if (response.data.success) {
        await fetchContentLibrary();
        closeContentModal();
        showMessage(editingContentId ? 'İçerik güncellendi!' : 'İçerik eklendi!', 'success');
        return true;
      } else {
        showMessage(response.data.error || 'İşlem başarısız', 'error');
        return false;
      }

    } catch (error) {
      console.error('İçerik kaydetme hatası:', error);

      if (error.response) {
        showMessage(error.response.data?.error || 'Sunucu hatası', 'error');
      } else if (error.request) {
        showMessage('Sunucuya bağlanılamadı', 'error');
      } else {
        showMessage('İçerik kaydedilemedi', 'error');
      }

      return false;
    } finally {
      setContentForm(prev => ({
        ...prev,
        uploadProgress: 0
      }));
    }
  };

  const uploadWithProgress = (url, method, formData) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setContentForm(prev => ({
            ...prev,
            uploadProgress: percentComplete
          }));
        }
      });

      xhr.upload.addEventListener('loadend', () => {
        setContentForm(prev => ({
          ...prev,
          uploadProgress: 100
        }));
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 200 || xhr.status === 201) {
          setContentForm(prev => ({
            ...prev,
            uploadProgress: 0
          }));

          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success) {
              await fetchContentLibrary();
              closeContentModal();
              showMessage(editingContentId ? 'İçerik güncellendi!' : 'İçerik başarıyla eklendi!', 'success');
              resolve(true);
            } else {
              showMessage(response.error || 'İşlem başarısız', 'error');
              resolve(false);
            }
          } catch (e) {
            showMessage('Sunucu yanıtı işlenemedi', 'error');
            resolve(false);
          }
        } else {
          setContentForm(prev => ({
            ...prev,
            uploadProgress: 0
          }));
          showMessage(`Hata: ${xhr.statusText}`, 'error');
          resolve(false);
        }
      });

      xhr.addEventListener('error', () => {
        setContentForm(prev => ({
          ...prev,
          uploadProgress: 0
        }));
        showMessage('Yükleme hatası', 'error');
        resolve(false);
      });

      xhr.open(method, url);

      const token = localStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.send(formData);
    });
  };

  const closeContentModal = () => {
    setIsContentModalOpen(false);
    setEditingContentId(null);
    setContentForm({
      title: '',
      content: '',
      tagIds: [],
      media: null,
      buttons: [],
      uploadProgress: 0,
      x_text: '',
      is_x_enabled: false,
      tiktok_caption: '',
      tiktok_hashtags: '',
      tiktok_allow_comments: true,
      tiktok_allow_duet: true,
      is_tiktok_enabled: false,
      instagram_caption: '',
      instagram_content_type: 'post',
      instagram_first_comment: '',
      instagram_collaborators: '',
      instagram_user_tags: '',
      is_instagram_enabled: false
    });
    setButtonForm([]);
  };

  const handleEditContent = async (content) => {
    try {
      const response = await axios.get(`${API_URL}/content-library/${content.id}`);
      const detailedContent = response.data.content;

      let parsedButtons = [];
      try {
        parsedButtons = detailedContent.buttons ? JSON.parse(detailedContent.buttons) : [];
      } catch (e) {
        parsedButtons = [];
      }

      let tagIds = [];
      if (detailedContent.content_tags && Array.isArray(detailedContent.content_tags)) {
        tagIds = detailedContent.content_tags.map(tag => tag.id);
      }

      setContentForm({
        title: detailedContent.title,
        content: detailedContent.content || '',
        tagIds: tagIds,
        media: null,
        buttons: parsedButtons,
        youtube_title: detailedContent.youtube_title || '',
        youtube_description: detailedContent.youtube_description || '',
        x_text: detailedContent.x_text || '',
        is_x_enabled: detailedContent.enable_for_x === 1,
        tiktok_caption: detailedContent.tiktok_caption || '',
        tiktok_hashtags: detailedContent.tiktok_hashtags || '',
        tiktok_allow_comments: detailedContent.tiktok_allow_comments === 1,
        tiktok_allow_duet: detailedContent.tiktok_allow_duet === 1,
        is_tiktok_enabled: detailedContent.is_tiktok_enabled === 1,
        instagram_caption: detailedContent.instagram_caption || '',
        instagram_content_type: detailedContent.instagram_content_type || 'post',
        instagram_first_comment: detailedContent.instagram_first_comment || '',
        instagram_collaborators: detailedContent.instagram_collaborators || '',
        instagram_user_tags: detailedContent.instagram_user_tags || '',
        is_instagram_enabled: detailedContent.is_instagram_enabled === 1,
        media_urls: detailedContent.media_urls || null,
        media_types: detailedContent.media_types || null,
        media_path: detailedContent.media_path || null,
        media_type: detailedContent.media_type || null,
        video_duration: detailedContent.video_duration || null,
        video_url: detailedContent.video_url || null,
        image_url: detailedContent.image_url || null,
        youtube_video_url: detailedContent.youtube_video_url || null
      });

      setButtonForm(parsedButtons);
      setEditingContentId(detailedContent.id);
      setIsContentModalOpen(true);
    } catch (error) {
      console.error('Edit content error:', error);
      showMessage('İçerik yüklenemedi', 'error');
    }
  };

  const handleDeleteContent = async (contentId) => {
    try {
      await axios.delete(`${API_URL}/content-library/${contentId}`);
      await fetchContentLibrary();
      showMessage('İçerik silindi!', 'success');
      setIsContentModalOpen(false);
      setEditingContentId(null);
      setContentForm({
        title: '',
        content: '',
        tagIds: [],
        media: null,
        buttons: []
      });
      setButtonForm([]);
    } catch (error) {
      showMessage(`Silme hatası: ${error.message}`, 'error');
    }
  };

  // Tag Operations
  const handleAddTag = async (newTag) => {
    try {
      const response = await axios.post(`${API_URL}/tags`, newTag);
      if (response.data.success) {
        setTags([...tags, response.data.tag]);
        showMessage('Etiket eklendi', 'success');
        return true;
      }
    } catch (error) {
      showMessage(error.response?.data?.error || 'Etiket eklenemedi', 'error');
      return false;
    }
  };

  const handleUpdateTag = async (tagId, updatedTag) => {
    try {
      const response = await axios.put(`${API_URL}/tags/${tagId}`, updatedTag);
      if (response.data.success) {
        setTags(tags.map(tag => tag.id === tagId ? response.data.tag : tag));
        showMessage('Etiket güncellendi', 'success');
        return true;
      }
    } catch (error) {
      showMessage(error.response?.data?.error || 'Etiket güncellenemedi', 'error');
      return false;
    }
  };

  const handleDeleteTag = async (tagId) => {
    return new Promise((resolve) => {
      toast((t) => (
        <div>
          <p style={{ marginBottom: '10px' }}>
            Bu etiketi silmek istediğinize emin misiniz?
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={async () => {
                toast.dismiss(t.id);
                try {
                  await axios.delete(`${API_URL}/tags/${tagId}`);
                  setTags(tags.filter(tag => tag.id !== tagId));
                  toast.success('Etiket silindi');
                  resolve(true);
                } catch (error) {
                  toast.error(error.response?.data?.error || 'Etiket silinemedi');
                  resolve(false);
                }
              }}
              style={{
                padding: '6px 12px',
                background: '#c17c74',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Sil
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(false);
              }}
              style={{
                padding: '6px 12px',
                background: '#6b6560',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              İptal
            </button>
          </div>
        </div>
      ), {
        duration: Infinity,
        position: 'top-center'
      });
    });
  };

  const handleNavigateToLibrary = (tagId) => {
    const tag = tags.find(t => t.id === tagId);
    if (tag) {
      setSelectedTagForLibrary(tagId);
      setActiveTab('library');
      setTimeout(() => {
        fetchContentLibrary(tag.name);
      }, 100);
    }
  };

  const handleNavigateToAutomation = (automationId) => {
    setActiveTab('automation');
    setPendingAutomationEdit(automationId);
    showMessage('Otomasyon düzenleme ekranına yönlendiriliyorsunuz...', 'info');
  };

  // Automation Operations
  const handleAutomationSubmit = async (formData) => {
    try {
      await axios.post(`${API_URL}/automation`, formData);
      showMessage('Otomasyon kuralı eklendi!', 'success');
      await fetchAutomationRules();
      return true;
    } catch (error) {
      showMessage(`Hata: ${error.message}`, 'error');
      return false;
    }
  };

  const toggleAutomationRule = async (id, currentStatus) => {
    try {
      await axios.put(`${API_URL}/automation/${id}`, {
        is_active: !currentStatus
      });
      await fetchAutomationRules();
      showMessage(currentStatus ? 'Kural durduruldu' : 'Kural aktifleştirildi', 'success');
    } catch (error) {
      showMessage(`Hata: ${error.message}`, 'error');
    }
  };

  const handleDeleteAutomation = async (id) => {
    if (window.confirm('Bu otomasyon kuralını silmek istediğinize emin misiniz?')) {
      try {
        await axios.delete(`${API_URL}/automation/${id}`);
        await fetchAutomationRules();
        showMessage('Otomasyon kuralı silindi!', 'success');
      } catch (error) {
        showMessage(`Silme hatası: ${error.message}`, 'error');
      }
    }
  };

  // Password change handler
  const handleChangePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${AUTH_URL}/change-password`, {
        currentPassword,
        newPassword
      });
      showMessage('Şifre başarıyla değiştirildi', 'success');
      return true;
    } catch (error) {
      showMessage(error.response?.data?.error || 'Şifre değiştirilemedi', 'error', 5000);
      return false;
    }
  };

  // Toggle submenu
  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  // Handle menu click - close sidebar on mobile
  const handleMenuClick = (tabId, parentId = null) => {
    setActiveTab(tabId);

    // Eğer parent varsa, parent'ı expand et
    if (parentId && !expandedMenus.includes(parentId)) {
      setExpandedMenus(prev => [...prev, parentId]);
    }

    if (window.innerWidth <= 1024) {
      setSidebarOpen(false);
    }
  };

  // Menu yapısı - submenu destekli
  const getMenuItems = () => {
    const items = [
      {
        id: 'library',
        label: 'İçerikler',
        icon: Icons.Library,
        hasSubmenu: true,
        submenu: [
          { id: 'brands', label: 'Markalar', icon: Icons.Brand },
          { id: 'copyright-free-music', label: 'Telifsiz Müzikler', icon: Icons.Music },
          { id: 'quick-share', label: 'Hızlı Paylaş', icon: Icons.QuickShare },
          { id: 'ai-video', label: 'AI Video', icon: Icons.AIVideo }
        ]
      },
      { id: 'channels', label: 'Kanallar', icon: Icons.Channels },
      { id: 'automation', label: 'Otomasyon', icon: Icons.Automation },
      { id: 'tags', label: 'Etiketler', icon: Icons.Tags },
      { id: 'statistics', label: 'İstatistikler', icon: Icons.Statistics },
      { id: 'live-studio', label: 'Canlı Stüdyo', icon: Icons.LiveStudio }
    ];

    if (user.role === 'root') {
      items.push({ id: 'admin', label: 'Yönetim', icon: Icons.Admin });
    }

    if (window.location.hostname === 'autogram.site') {
      items.push({ id: 'mail', label: 'Mail Yönetimi', icon: Icons.Mail });
    }

    items.push({ id: 'site-tracking', label: 'Site Takip', icon: Icons.SiteTracking });

    return items;
  };

  // Render menu item
  const renderMenuItem = (item) => {
    const IconComponent = item.icon;
    const isExpanded = expandedMenus.includes(item.id);
    const isActive = activeTab === item.id;
    const hasActiveChild = item.submenu?.some(sub => activeTab === sub.id);

    return (
      <div key={item.id} className="sidebar-menu-group">
        <button
          className={`sidebar-item ${isActive || hasActiveChild ? 'active' : ''} ${item.hasSubmenu ? 'has-submenu' : ''}`}
          onClick={() => {
            if (item.hasSubmenu) {
              toggleSubmenu(item.id);
              handleMenuClick(item.id);
            } else {
              handleMenuClick(item.id);
            }
          }}
        >
          <span className="sidebar-icon">
            <IconComponent />
          </span>
          <span className="sidebar-label">{item.label}</span>
          {item.hasSubmenu && (
            <span className={`sidebar-chevron ${isExpanded ? 'expanded' : ''}`}>
              <Icons.ChevronDown />
            </span>
          )}
          {(isActive && !item.hasSubmenu) && <span className="sidebar-active-indicator" />}
        </button>

        {/* Submenu */}
        {item.hasSubmenu && (
          <div className={`sidebar-submenu ${isExpanded ? 'expanded' : ''}`}>
            {item.submenu.map(subItem => {
              const SubIconComponent = subItem.icon;
              const isSubActive = activeTab === subItem.id;

              return (
                <button
                  key={subItem.id}
                  className={`sidebar-subitem ${isSubActive ? 'active' : ''}`}
                  onClick={() => handleMenuClick(subItem.id, item.id)}
                >
                  <span className="sidebar-subicon">
                    <SubIconComponent />
                  </span>
                  <span className="sidebar-sublabel">{subItem.label}</span>
                  {isSubActive && <span className="sidebar-active-indicator" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard">
      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/favicon.png" alt="Autogram" />
            <span>Autogram</span>
          </div>
          <button
            className="sidebar-close"
            onClick={() => setSidebarOpen(false)}
          >
            <Icons.Close />
          </button>
        </div>

        <nav className="sidebar-nav">
          {getMenuItems().map(item => renderMenuItem(item))}
        </nav>

        <div className="sidebar-footer">
          <div
            className="sidebar-user"
            onClick={() => handleMenuClick('profile')}
          >
            <span className="sidebar-user-icon">
              {user.role === 'root' ? <Icons.Crown /> : <Icons.Profile />}
            </span>
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{user.username}</span>
              <span className="sidebar-user-role">{user.role === 'root' ? 'Yönetici' : 'Kullanıcı'}</span>
            </div>
          </div>
          <button className="sidebar-logout" onClick={onLogout}>
            <Icons.Logout />
            <span>Çıkış</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        {/* Mobile Header */}
        <header className="mobile-header">
          <button
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
          >
            <Icons.Menu />
          </button>
          <div className="mobile-logo">
            <img src="/favicon.png" alt="Autogram" />
            <span>Autogram</span>
          </div>
          <div className="mobile-user" onClick={() => handleMenuClick('profile')}>
            {user.role === 'root' ? <Icons.Crown /> : <Icons.Profile />}
          </div>
        </header>

        {/* Content Area */}
        <div className="content">
          {loading ? (
            <div className="loading-circular">
              <div className="circular-loader">
                <svg viewBox="0 0 100 100">
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#b87333" />
                      <stop offset="100%" stopColor="#d4956a" />
                    </linearGradient>
                  </defs>
                  <circle className="bg" cx="50" cy="50" r="45" />
                  <circle className="progress" cx="50" cy="50" r="45" />
                </svg>
              </div>
              <p>İçerikler hazırlanıyor...</p>
            </div>
          ) : (
            <>
              {/* Library Tab */}
              {activeTab === 'library' && (
                <LibraryTab
                  contentLibrary={contentLibrary}
                  contentPagination={contentPagination}
                  onPageChange={(page, sort, search) => fetchContentLibrary(search || '', page, sort)}
                  onAddContent={() => {
                    setContentForm({
                      title: '',
                      content: '',
                      tagIds: [],
                      media: null,
                      buttons: []
                    });
                    setButtonForm([]);
                    setEditingContentId(null);
                    setIsContentModalOpen(true);
                  }}
                  onEditContent={handleEditContent}
                  onDeleteContent={handleDeleteContent}
                  onShowHistory={(content) => {
                    setSelectedContentForHistory(content);
                    setHistoryModalOpen(true);
                  }}
                  onSearch={(tag, page, sort) => fetchContentLibrary(tag, page || 1, sort || 'updated_at_desc')}
                  tags={tags}
                  API_URL={API_URL}
                  onRefreshContent={(page, sort) => fetchContentLibrary('', page || 1, sort || 'updated_at_desc')}
                  selectedTagForLibrary={selectedTagForLibrary}
                  clearFilterTag={() => setSelectedTagForLibrary(null)}
                  contentTagStats={contentTagStats}
                />
              )}

              {/* Copyright Free Music Tab */}
              {activeTab === 'copyright-free-music' && (
                <CopyrightFreeMusicTab
                  API_URL={API_URL}
                  showMessage={showMessage}
                />
              )}

              {/* Brands Tab */}
              {activeTab === 'brands' && (
                <BrandsTab
                  API_URL={API_URL}
                  showMessage={showMessage}
                />
              )}

              {/* Quick Share Tab */}
              {activeTab === 'quick-share' && (
                <QuickShareTab
                  channels={channels}
                  showMessage={showMessage}
                  API_URL={API_URL}
                />
              )}

              {/* AI Video Tab */}
              {activeTab === 'ai-video' && (
                <AIVideoTab
                  API_URL={API_URL}
                  showMessage={showMessage}
                />
              )}

              {/* Channels Tab */}
              {activeTab === 'channels' && (
                <ChannelsTab
                  channels={channels}
                  channelStats={channelStats}
                  loadingStats={loadingStats}
                  mtprotoStatus={mtprotoStatus}
                  onAddChannel={() => {
                    setChannelForm({ name: '', chat_id: '', channel_tags: [], description: '' });
                    setEditingChannelId(null);
                    setIsChannelModalOpen(true);
                  }}
                  onEditChannel={handleEditChannel}
                  onDeleteChannel={handleDeleteChannel}
                  onFetchChannelStats={fetchChannelStats}
                  onRefreshChannels={handleRefreshChannels}
                  showMessage={showMessage}
                  BASE_URL={BASE_URL}
                  onNavigateToLibrary={handleNavigateToLibrary}
                  onNavigateToAutomation={handleNavigateToAutomation}
                />
              )}

              {/* Tags Tab */}
              {activeTab === 'tags' && (
                <TagsTab
                  tags={tags}
                  contents={contentLibrary}
                  contentTagStats={contentTagStats}
                  onAddTag={handleAddTag}
                  onUpdateTag={handleUpdateTag}
                  onDeleteTag={handleDeleteTag}
                  showMessage={showMessage}
                  API_URL={API_URL}
                />
              )}

              {/* Automation Tab */}
              {activeTab === 'automation' && (
                <AutomationTab
                  automationRules={automationRules}
                  channels={channels}
                  contentLibrary={contentLibrary}
                  onSubmit={handleAutomationSubmit}
                  onToggle={toggleAutomationRule}
                  onDelete={handleDeleteAutomation}
                  showMessage={showMessage}
                  pendingEditId={pendingAutomationEdit}
                  onEditComplete={() => setPendingAutomationEdit(null)}
                />
              )}

              {/* Statistics Tab */}
              {activeTab === 'statistics' && (
                <StatisticsTab
                  statistics={statistics}
                  channels={channels}
                  channelStats={channelStats}
                  loadingStats={loadingStats}
                  mtprotoStatus={mtprotoStatus}
                  onFetchChannelStats={fetchChannelStats}
                  onRefreshChannels={handleRefreshChannels}
                />
              )}

              {/* Admin Panel */}
              {activeTab === 'admin' && user.role === 'root' && (
                <AdminPanel user={user} />
              )}

              {activeTab === 'mail' && (
                <MailManagementTab />
              )}

              {activeTab === 'site-tracking' && (
                <SiteTrackingTab
                  API_URL={API_URL}
                  showMessage={showMessage}
                />
              )}

              {/* Live Studio Tab */}
              {activeTab === 'live-studio' && (
                <LiveStudioTab
                  API_URL={API_URL}
                />
              )}

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <ProfileTab
                  user={user}
                  statistics={statistics}
                  automationRules={automationRules}
                  onChangePassword={handleChangePassword}
                  onTabChange={setActiveTab}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <ChannelModal
        isOpen={isChannelModalOpen}
        onClose={() => {
          setIsChannelModalOpen(false);
          setEditingChannelId(null);
          setChannelForm({ name: '', chat_id: '', channel_tags: [], description: '' });
        }}
        channelForm={channelForm}
        setChannelForm={setChannelForm}
        onSubmit={handleChannelSubmit}
        onDelete={handleDeleteChannel}
        editingChannelId={editingChannelId}
        tags={tags}
        mtprotoStatus={mtprotoStatus}
        channelType={null}
      />

      <ContentModal
        isOpen={isContentModalOpen}
        onClose={() => {
          setIsContentModalOpen(false);
          setEditingContentId(null);
          setContentForm({
            title: '',
            content: '',
            tagIds: [],
            media: null,
            buttons: []
          });
          setButtonForm([]);
        }}
        contentForm={contentForm}
        setContentForm={setContentForm}
        buttonForm={buttonForm}
        setButtonForm={setButtonForm}
        onSubmit={handleContentSubmit}
        editingContentId={editingContentId}
        onDelete={handleDeleteContent}
        tags={tags}
      />

      <HistoryModal
        isOpen={historyModalOpen}
        onClose={() => {
          setHistoryModalOpen(false);
          setSelectedContentForHistory(null);
        }}
        contentId={selectedContentForHistory?.id}
        contentTitle={selectedContentForHistory?.title}
      />
    </div>
  );
};

export default Dashboard;